import { useEffect, useRef, useState } from 'react';
import type {
  CheckoutInterval,
  CheckoutPlan,
} from '@klab-berlin/api-sdk/lib/types/requests/Billing';
import service from '../../../../services/billing.service';
import { VALID_INTERVALS } from '../checkout.constants';
import { mapErrorToKind, resolveCheckoutAttribution } from '../checkout.helpers';
import type { CheckoutErrorKind } from '../checkout.types';

export type SessionSecrets = Record<CheckoutInterval, string | null>;

const EMPTY_SECRETS: SessionSecrets = { monthly: null, yearly: null };

interface UseCheckoutSessionsParams {
  plan: CheckoutPlan | null;
  trial: boolean;
  // SM-273: NOT a reactive dependency — a toggle must not recreate the session
  // pair (that remounts <PaymentElement>, wiping entered card details). Captured
  // via a ref, so it only ever seeds the metadata with the mount-time state.
  // SM-284: the page pushes the authoritative final state via
  // `updateCheckoutSession` right before confirm.
  newsletterConsent: boolean;
  /** False while the checkout link is invalid (no plan/interval) — skips creation. */
  enabled: boolean;
}

interface UseCheckoutSessionsResult {
  secrets: SessionSecrets;
  /**
   * ISO 3166-1 alpha-2 country the legacy API resolved from the client IP (the
   * same value used for tax-rate matching), used to seed the Stripe
   * billing-address country default so the first invoice's country matches the
   * applied tax rate (SM-252). Defaults to `'DE'` until the sessions land and
   * whenever the response carries no country.
   */
  country: string;
  errorCode: CheckoutErrorKind | null;
  setErrorCode: (kind: CheckoutErrorKind | null) => void;
}

const DEFAULT_COUNTRY = 'DE';

/**
 * Owns the Stripe checkout sessions. We create one session per billing interval
 * (monthly + yearly) up front so both <PaymentElement>s can stay mounted — the
 * user can toggle interval instantly without reloading Stripe or losing card
 * input. Sessions are promo-independent: a promo code is applied/removed in place
 * on the live session (see <CheckoutPaymentElement>), so changing it never
 * recreates the session and never wipes entered card details. Sessions are only
 * recreated when plan or trial changes.
 *
 * The session is deliberately created WITHOUT a promo code: Stripe treats
 * `discounts` (a baked-in code) and `allow_promotion_codes` (needed for
 * `applyPromotionCode`) as mutually exclusive (kimuno stripe adapter). Baking a
 * link-borne promo in here would speed first paint but permanently disable
 * in-place apply/remove for that session — so a URL promo is applied in place on
 * mount instead, costing one extra round-trip but keeping the code editable.
 */
export function useCheckoutSessions({
  plan,
  trial,
  newsletterConsent,
  enabled,
}: UseCheckoutSessionsParams): UseCheckoutSessionsResult {
  const [secrets, setSecrets] = useState<SessionSecrets>(EMPTY_SECRETS);
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY);
  const [errorCode, setErrorCode] = useState<CheckoutErrorKind | null>(null);

  // Latest consent, read (not depended on) when a session is created — see the
  // `newsletterConsent` param doc above (SM-273).
  const newsletterConsentRef = useRef(newsletterConsent);
  newsletterConsentRef.current = newsletterConsent;

  useEffect(() => {
    if (!plan || !enabled) return;
    let cancelled = false;
    setErrorCode(null);

    (async () => {
      try {
        // SM-309: resolved per session-creation (not a hook param) so a
        // plan/trial recreation picks up fresh last-touch values; the same
        // object goes to both intervals. Omitted entirely when nothing
        // resolves (no cookie / declined consent / clean URL).
        const attribution = resolveCheckoutAttribution();
        // Destructure order is coupled to VALID_INTERVALS = ['monthly', 'yearly'].
        const [monthly, yearly] = await Promise.all(
          VALID_INTERVALS.map((interval) =>
            service.createCheckoutSession({
              plan,
              interval,
              trial,
              newsletterConsent: newsletterConsentRef.current,
              ...(attribution ? { attribution } : {}),
            }),
          ),
        );
        if (cancelled) return;
        setSecrets({ monthly: monthly.clientSecret, yearly: yearly.clientSecret });
        setCountry(monthly.country ?? yearly.country ?? DEFAULT_COUNTRY);
        setErrorCode(null);
      } catch (err) {
        if (cancelled) return;
        setSecrets(EMPTY_SECRETS);
        setErrorCode(mapErrorToKind(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [plan, trial, enabled]);

  return { secrets, country, errorCode, setErrorCode };
}
